import type { NotificationType } from '../constants/notificationTypes';

type AuthorizedPersonLike = {
  id?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  email?: string | null;
  notificationTypes?: string[] | null;
  controlAddressIds?: Array<number | string> | null;
};

type ProposalLike = {
  address?: {
    id?: number | string | null;
  } | null;
  addressId?: number | string | null;
  authorizedPersons?: AuthorizedPersonLike[] | null;
};

export type AuthorizedRecipientOption = {
  value: string;
  label: string;
  person: AuthorizedPersonLike;
};

const toNumericId = (value: number | string | null | undefined): number | null => {
  if (value == null) {
    return null;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }
  return numericValue;
};

const normalizeNotificationType = (value: string | null | undefined): string | null => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue.toUpperCase() : null;
};

const normalizeEmail = (value: string | null | undefined): string | null => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue.toLowerCase() : null;
};

const buildPersonLabel = (person: AuthorizedPersonLike, email: string): string => {
  const firstName = person.firstName?.trim() ?? '';
  const lastName = person.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const position = person.position?.trim();

  if (fullName && position) {
    return `${fullName} (${position}) - ${email}`;
  }
  if (fullName) {
    return `${fullName} - ${email}`;
  }
  return email;
};

export const getProposalAddressId = (proposal: ProposalLike | null | undefined): number | null => {
  return toNumericId(proposal?.address?.id ?? proposal?.addressId ?? null);
};

export const getMatchingAuthorizedPersons = (
  proposal: ProposalLike | null | undefined,
  notificationType?: NotificationType,
): AuthorizedPersonLike[] => {
  const proposalAddressId = getProposalAddressId(proposal);
  const normalizedNotificationType = normalizeNotificationType(notificationType);
  const proposalAuthorizedPersons = proposal?.authorizedPersons;
  const authorizedPersons = Array.isArray(proposalAuthorizedPersons)
    ? proposalAuthorizedPersons
    : [];
  if (!proposalAddressId || authorizedPersons.length === 0) {
    return [];
  }

  return authorizedPersons.filter((person) => {
    const controlAddressIds = Array.isArray(person?.controlAddressIds)
      ? person.controlAddressIds
      : [];
    const notificationTypes = Array.isArray(person?.notificationTypes)
      ? person.notificationTypes
      : [];
    const hasControlAddressMatch = controlAddressIds.some(
      (controlAddressId) => toNumericId(controlAddressId) === proposalAddressId,
    );
    if (!hasControlAddressMatch) {
      return false;
    }
    if (!normalizedNotificationType) {
      return true;
    }
    return notificationTypes.some(
      (type) => normalizeNotificationType(type) === normalizedNotificationType,
    );
  });
};

export const hasMatchingAuthorizedRecipients = (
  proposal: ProposalLike | null | undefined,
  notificationType?: NotificationType,
): boolean => getMatchingAuthorizedPersons(proposal, notificationType).length > 0;

export const getMatchingAuthorizedEmailOptions = (
  proposal: ProposalLike | null | undefined,
  notificationType?: NotificationType,
): AuthorizedRecipientOption[] => {
  const optionsByEmail = new Map<string, AuthorizedRecipientOption>();
  getMatchingAuthorizedPersons(proposal, notificationType).forEach((person) => {
    const normalizedEmail = normalizeEmail(person?.email);
    if (!normalizedEmail || optionsByEmail.has(normalizedEmail)) {
      return;
    }
    optionsByEmail.set(normalizedEmail, {
      value: normalizedEmail,
      label: buildPersonLabel(person, person.email?.trim() ?? normalizedEmail),
      person,
    });
  });
  return Array.from(optionsByEmail.values());
};
